import React, { useEffect, useState } from "react";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { db } from "../../services/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { tuitionConfigs } from "../../data/tuitionConfigs";

export default function TuitionPricingManager() {
  const [campus, setCampus] = useState("gapan");
  const [schoolYear, setSchoolYear] = useState(new Date().getFullYear() + "-" + (new Date().getFullYear() + 1));
  const [jsonText, setJsonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState("");

  function append(t) { setLog(l => (l ? l + "\n" : "") + t); }

  async function load() {
    setBusy(true);
    setLog("");
    try {
      let data = null;
      if (!USE_LOCAL_FALLBACK) {
        const snap = await getDoc(doc(db, "gradePricing", `${campus}-${schoolYear}`));
        data = snap.exists() ? snap.data() : null;
      }
      if (!data) {
        // fallback to local static config
        const levels = tuitionConfigs[campus]?.levels || [];
        data = { campus, schoolYear, levels };
      }
      setJsonText(JSON.stringify(data.levels || [], null, 2));
      append("Loaded pricing.");
    } catch (e) {
      append(`Load failed: ${e.message || String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setLog("");
    try {
      let levels = [];
      try {
        const parsed = JSON.parse(jsonText);
        if (!Array.isArray(parsed)) throw new Error("JSON must be an array of { name, paymentModes }");
        levels = parsed;
      } catch (e) {
        append(`Invalid JSON: ${e.message}`);
        setBusy(false);
        return;
      }
      const payload = { campus, schoolYear, levels, updatedAt: new Date().toISOString() };
      if (!USE_LOCAL_FALLBACK) {
        await setDoc(doc(db, "gradePricing", `${campus}-${schoolYear}`), payload, { merge: true });
        append("Saved to Firestore.");
      } else {
        append("Running in local fallback; Firestore save skipped.");
      }
      // cache for fast reads
      localStorage.setItem(`gradePricing_cache_${campus}`, JSON.stringify(payload));
      append("Cached locally for immediate use.");
    } catch (e) {
      append(`Save failed: ${e.message || String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); }, [campus, schoolYear]);

  function useCodeDefaults() {
    try {
      const levels = tuitionConfigs[campus]?.levels || [];
      setJsonText(JSON.stringify(levels, null, 2));
      append("Loaded code defaults for " + campus + ".");
    } catch (e) {
      append("Failed: " + (e.message || String(e)));
    }
  }

  async function saveFromCode() {
    setBusy(true);
    setLog("");
    try {
      const levels = tuitionConfigs[campus]?.levels || [];
      const payload = { campus, schoolYear, levels, updatedAt: new Date().toISOString() };
      if (!USE_LOCAL_FALLBACK) {
        await setDoc(doc(db, "gradePricing", `${campus}-${schoolYear}`), payload, { merge: true });
        append("Saved code defaults to Firestore.");
      } else {
        append("Running in local fallback; Firestore save skipped.");
      }
      localStorage.setItem(`gradePricing_cache_${campus}`, JSON.stringify(payload));
      append("Cached locally for immediate use.");
      setJsonText(JSON.stringify(levels, null, 2));
    } catch (e) {
      append(`Save failed: ${e.message || String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container py-4">
      <h3 className="mb-3">Tuition Pricing Manager</h3>
      <div className="alert alert-info">
        Edit pricing per campus and school year. Pages will use this pricing immediately and cache it locally.
      </div>
      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <label className="form-label">Campus</label>
          <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
            <option value="gapan">gapan</option>
            <option value="bulacan">bulacan</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">School Year</label>
          <input className="form-control" value={schoolYear} onChange={e => setSchoolYear(e.target.value)} placeholder="e.g., 2025-2026" />
        </div>
      </div>
      <div className="mb-2">
        <label className="form-label">Levels JSON (array of {`{ name, paymentModes: [{ mode, tuitionFee, miscFee, discount, totalDue, schedule: [...] }] }`})</label>
        <textarea className="form-control" rows={18} value={jsonText} onChange={e => setJsonText(e.target.value)} style={{ fontFamily: "monospace" }} />
      </div>
      <div className="d-flex gap-2">
        <button className="btn btn-primary" disabled={busy} onClick={save}>Save</button>
        <button className="btn btn-outline-secondary" disabled={busy} onClick={load}>Reload</button>
        <button className="btn btn-outline-info" disabled={busy} onClick={useCodeDefaults}>Use Code Defaults</button>
        <button className="btn btn-success" disabled={busy} onClick={saveFromCode}>Save From Code</button>
      </div>
      {log && <div className="mt-3 alert alert-secondary"><pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{log}</pre></div>}
    </div>
  );
}
